import { CademyError } from '@shared/domain-shared';
import { CheckoutState, OriginString } from '@shared/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    initialiseBookingApplicationRequestFlow,
    initialiseBookingFlow,
    initialiseDateBookingRequestFlow,
    initialiseGroupBookingRequestFlow,
    initialiseOrderFlow,
    initialiseRequestFlow,
} from '../../data/initialiseFunctions';
import { useGetUtms } from '../../../../hooks/useUtms';

export type UseInitialiseCheckoutProps = {
    courseId: string;
    origin: OriginString;
    flow: CheckoutState['flow'];
    initialCheckoutId?: string;
    occurrenceId?: string;
    onCheckoutIdChange?: (newCheckoutId: string) => void;
};

export type UseInitialiseCheckoutResponse =
    | {
          initialised: false;
          checkoutState: null;
          error: CademyError | null;
      }
    | {
          initialised: true;
          checkoutState: CheckoutState;
          error: CademyError | null;
      };

export const useInitializeCheckout = ({
    courseId,
    origin,
    flow,
    initialCheckoutId,
    occurrenceId,
    onCheckoutIdChange,
}: UseInitialiseCheckoutProps): UseInitialiseCheckoutResponse => {
    const initialising = useRef(false);
    const [initialised, setInitialised] = useState(false);
    const [checkoutId, setCheckoutId] = useState<string | null>(initialCheckoutId || null);
    const [checkoutState, setCheckoutState] = useState<CheckoutState | null>(null);
    const [error, setError] = useState<CademyError | null>(null);
    const getUtms = useGetUtms();

    const getInitialCheckoutState = useCallback(async (): Promise<CheckoutState> => {
        const utms = getUtms();
        const bookedOnURL = window.location.origin + window.location.pathname;
        switch (flow) {
            case 'booking':
                return initialiseBookingFlow(
                    checkoutId,
                    courseId,
                    origin,
                    bookedOnURL,
                    utms,
                    occurrenceId
                );
            case 'order':
                return initialiseOrderFlow(checkoutId, courseId, origin, bookedOnURL, utms);
            case 'request':
                return initialiseRequestFlow(checkoutId, courseId, origin, bookedOnURL, utms);
            case 'dateBookingRequest':
                return initialiseDateBookingRequestFlow(
                    checkoutId,
                    courseId,
                    origin,
                    bookedOnURL,
                    utms
                );
            case 'groupBookingRequest':
                return initialiseGroupBookingRequestFlow(
                    checkoutId,
                    courseId,
                    origin,
                    bookedOnURL,
                    utms
                );
            case 'bookingApplication':
                return initialiseBookingApplicationRequestFlow(
                    checkoutId,
                    courseId,
                    origin,
                    bookedOnURL,
                    utms,
                    occurrenceId
                );
        }
    }, [checkoutId, courseId, origin, getUtms, flow, occurrenceId]);

    useEffect(() => {
        if (initialised === true) {
            return;
        }
        if (initialising.current === true) {
            return;
        }
        initialising.current = true;
        setError(null);
        getInitialCheckoutState()
            .then((initialState) => {
                setCheckoutState(initialState);
                setCheckoutId(initialState._id);
                setInitialised(true);
            })
            .catch((error) => {
                console.error(error);
                setError(CademyError.fromUnknown(error));
            })
            .finally(() => {
                initialising.current = false;
            });
    }, [initialised, getInitialCheckoutState, setCheckoutId]);

    useEffect(() => {
        setInitialised(false);
    }, [courseId]);

    useEffect(() => {
        if (checkoutId && checkoutId !== initialCheckoutId && onCheckoutIdChange) {
            onCheckoutIdChange(checkoutId);
        }
    }, [initialCheckoutId, checkoutId, onCheckoutIdChange]);

    if (initialised === false || checkoutState === null) {
        return {
            initialised: false,
            checkoutState: null,
            error,
        };
    }

    return {
        initialised: true,
        checkoutState: checkoutState,
        error,
    };
};
